import * as Yup from 'yup'
import isNil from 'lodash.isnil'
import { map } from 'lodash'
import { CashJournalValues, JournalVariation } from './Types'
import { DistributionConstraints, ContributionConstraints, TaxWithholdingValueType } from '@Types'
import { NONE_VALUE } from '../../utils/optionBuilders'
import moment from 'moment'
import { TransferFrequencyType, TransferRecurrenceType, WhenToSendUnion } from '../TransferFrequency/Types'

const isDateValid = (dateValue: string | Date) => {
  const formattedDate = new Date(dateValue).toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  return !isNaN(new Date(dateValue).getTime()) && moment(formattedDate, 'MM/DD/YYYY', true).isValid()
}

export const isNextDisabled = (formVariation: JournalVariation, formik: { values: CashJournalValues }) => {
  const { values } = formik
  let isDisabled: boolean = false
  if (formVariation === JournalVariation.indToIra) {
    if (!values.contributionType || values.contributionType === NONE_VALUE) {
      isDisabled = true
    }
  }
  if (formVariation === JournalVariation.iraToInd) {
    if (!values.distributionReason) {
      isDisabled = true
    }
  }
  if (formVariation === JournalVariation.iraToIra) {
    if (values.contributionReason === NONE_VALUE || values.distributionReason === '') {
      isDisabled = true
    }
  }
  if (formVariation === JournalVariation.incomplete) {
    isDisabled = true
  }
  return isDisabled
}

export const cashJournalSchema = (
  iraDistributionConstraints?: DistributionConstraints | null,
  iraContributionConstraints?: ContributionConstraints | null,
  amountAvailable?: number,
  hasRestrictions?: boolean
) =>
  Yup.object({
    totalBalanceCheckbox: Yup.boolean().required().default(false),
    fromAccount: Yup.string().required('Required'),
    toAccount: Yup.string()
      .required('Required')
      .test('exceed-max-length', 'Maximum 20 characters', (value) => {
        const test = !!value && value.length > 20 ? false : true
        return test
      })
      .default(''),
    transferAmountRaw: Yup.number()
      .when('totalBalanceCheckbox', {
        is: false,
        then: Yup.number()
          .nullable()
          .required('Required')
          .typeError('')
          .min(0.01, 'Amount must be greater than $0.00')
          .default(0)
      })
      .default(0),
    transferAmountDisplay: Yup.string()
      .when('totalBalanceCheckbox', {
        is: false,
        then: Yup.string().required('Required')
      })
      .default(''),

    ...(!isNil(iraDistributionConstraints) && {
      distributionReason: Yup.string().required('Required'),
      federalTaxWithholdingType: Yup.mixed<TaxWithholdingValueType | ''>().default(''),
      federalTaxWithholdingAmountRaw: Yup.number().when('federalTaxWithholdingType', {
        is: (type: string) => type && type.length > 0,
        then: Yup.number()
          .when('federalTaxWithholdingType', {
            is: TaxWithholdingValueType.Fixed,
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than $0.00')
          })
          .when('federalTaxWithholdingType', {
            is: TaxWithholdingValueType.Percent,
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than 0%')
              .max(100, 'Amount must be lower than 100%')
          })
      }),
      federalTaxWithholdingAmountDisplay: Yup.string()
        .when('federalTaxWithholdingType', {
          is: (type: string) => type && type.length > 0 && type === 'FIXED',
          then: Yup.string().required('Required')
        })
        .default(''),

      stateTaxWithholdingType: Yup.mixed<TaxWithholdingValueType>().oneOf(map(TaxWithholdingValueType)),
      stateTaxWithholdingAmountRaw: Yup.number().when('stateTaxWithholdingType', {
        is: (type: string) => type && type.length > 0,
        then: Yup.number()
          .when('stateTaxWithholdingType', {
            is: TaxWithholdingValueType.Fixed,
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than $0.00')
          })
          .when('stateTaxWithholdingType', {
            is: TaxWithholdingValueType.Percent,
            then: Yup.number()
              .nullable()
              .typeError('')
              .required('Required')
              .min(0.01, 'Amount must be greater than 0%')
              .max(100, 'Amount must be lower than 100%')
          })
      })
    }),
    stateTaxWithholdingAmountDisplay: Yup.string().when('stateTaxWithholdingType', {
      is: (type: string) => type && type.length > 0 && type === 'FIXED',
      then: Yup.string().required('Required')
    }),
    contributionReason: Yup.string().when('iraContributionConstraints', {
      is: () => !isNil(iraContributionConstraints) && !isNil(iraDistributionConstraints),
      then: Yup.string().required('Required')
    }),
    contributionType: Yup.string().when('iraContributionConstraints', {
      is: () => !isNil(iraContributionConstraints) && isNil(iraDistributionConstraints),
      then: Yup.string().required('Required')
    }),

    contributionYear: Yup.lazy(() => {
      if (!isNil(iraContributionConstraints)) {
        return Yup.string().when(['contributionType'], {
          is: (contributionType: string) => {
            const doesSelectedTypeAllowPrevious = iraContributionConstraints?.validContributions.find(
              (item) => item.reason === contributionType
            )?.previousYearContributionAllowed
            return !iraContributionConstraints?.onlyAllowCurrentYearContribution && !!doesSelectedTypeAllowPrevious
          },
          then: Yup.string().required('Required').default('CURRENT')
        })
      }
      return Yup.string().default('')
    }),
    transferRecurrence: Yup.mixed<TransferRecurrenceType>().default('ONE_TIME'),
    whenToSend: Yup.mixed<WhenToSendUnion>().default('IMMEDIATELY'),
    transferScheduledDate: Yup.string()
      .when('whenToSend', {
        is: 'IN_THE_FUTURE',
        then: Yup.string()
          .test({
            message: () => '',
            test: function (value, context) {
              if (!!value) {
                return isDateValid(value)
              }
              return true
            }
          })
          .required('Required')
          .default('')
      })
      .default(''),
    transferFrequency: Yup.mixed<TransferFrequencyType>()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.mixed<TransferFrequencyType>().required('Required').default('WEEKLY')
      })
      .default('WEEKLY'),
    transferStartDate: Yup.string()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.string()
          .test({
            message: () => 'Invalid date',
            test: function (value) {
              if (!!value) {
                return isDateValid(value)
              }
              return true
            }
          })
          .test({
            message: () => 'Recurring transfers not allowed for total balance transfers',
            test: function (value, context) {
              return !context.parent.totalBalanceCheckbox
            }
          })
          .required('Required')
          .default('')
      })
      .default(''),
    numberOfTransfers: Yup.number()
      .when('transferRecurrence', {
        is: 'RECURRING',
        then: Yup.number().max(100, 'Maximum 100').min(1, 'Value must be greater than 0').default(undefined)
      })
      .default(undefined),
    hasRestrictions: Yup.boolean().test({
      message: 'hasRestrictions',
      test: () => !hasRestrictions
    })
  })
